import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';

const EMICalculator = () => {
  const [loanType, setLoanType] = useState('personal');
  const [principal, setPrincipal] = useState(100000);
  const [interestRate, setInterestRate] = useState(8);
  const [tenure, setTenure] = useState(12);
  const [emi, setEmi] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  const brandColor = '#340C7F';

  // Loan type configurations with defaults and ranges
  const loanTypes = {
    personal: { 
      name: 'Personal Loan', 
      minAmount: 500, 
      maxAmount: 5000000,
      defaultAmount: 500,
      minRate: 8,
      maxRate: 30,
      defaultRate: 16,
      minTenure: 6,
      maxTenure: 60,
      defaultTenure: 12,
      step: 1000
    },
    home: { 
      name: 'Home Loan', 
      minAmount: 500000, 
      maxAmount: 50000000,
      defaultAmount: 500000,
      minRate: 8,
      maxRate: 15,
      defaultRate: 8.5,
      minTenure: 12,
      maxTenure: 360,
      defaultTenure: 240,
      step: 100000
    },
    lap: { 
      name: 'Loan Against Property', 
      minAmount: 500000, 
      maxAmount: 50000000,
      defaultAmount: 500000,
      minRate: 8,
      maxRate: 18,
      defaultRate: 10,
      minTenure: 12,
      maxTenure: 240,
      defaultTenure: 180,
      step: 100000
    },
    gold: { 
      name: 'Gold Loan', 
      minAmount: 50000, 
      maxAmount: 2000000,
      defaultAmount: 50000,
      minRate: 8,
      maxRate: 20,
      defaultRate: 13,
      minTenure: 6,
      maxTenure: 36,
      defaultTenure: 12,
      step: 10000
    }
  };

  // Update defaults when loan type changes
  useEffect(() => {
    const currentLoan = loanTypes[loanType];
    setPrincipal(currentLoan.defaultAmount);
    setInterestRate(currentLoan.defaultRate);
    setTenure(currentLoan.defaultTenure);
  }, [loanType]);

  // Calculate EMI - smooth and accurate
  const calculateEMI = () => {
    if (principal <= 0 || interestRate <= 0 || tenure <= 0) {
      setEmi(0);
      setTotalAmount(0);
      setTotalInterest(0);
      return;
    }

    const monthlyRate = interestRate / 12 / 100;
    let monthlyEMI;

    if (monthlyRate === 0) {
      monthlyEMI = principal / tenure;
    } else {
      const numerator = principal * monthlyRate * Math.pow(1 + monthlyRate, tenure);
      const denominator = Math.pow(1 + monthlyRate, tenure) - 1;
      monthlyEMI = numerator / denominator;
    }

    const total = monthlyEMI * tenure;
    const interest = total - principal;

    setEmi(Math.round(monthlyEMI));
    setTotalAmount(Math.round(total));
    setTotalInterest(Math.round(interest));
  };

  // Calculate on any change
  useEffect(() => {
    calculateEMI();
  }, [principal, interestRate, tenure]);

  // Format currency
  const formatAmount = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Get preset amounts for current loan type
  const getPresetAmounts = () => {
    const currentLoan = loanTypes[loanType];
    const presets = [];
    
    if (loanType === 'personal') {
      presets.push(50000, 100000, 200000, 500000, 1000000);
    } else if (loanType === 'home' || loanType === 'lap') {
      presets.push(1000000, 2500000, 5000000, 10000000, 20000000);
    } else if (loanType === 'gold') {
      presets.push(50000, 100000, 200000, 500000, 1000000);
    }
    
    return presets.filter(amount => amount >= currentLoan.minAmount && amount <= currentLoan.maxAmount);
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="p-3 rounded-full" style={{ backgroundColor: `${brandColor}20` }}>
              <Calculator className="h-8 w-8" style={{ color: brandColor }} />
            </div>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            EMI <span style={{ color: brandColor }}>Calculator</span>
          </h2>
          <p className="text-xl text-gray-600">Calculate your monthly loan payments with interactive sliders</p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            
            {/* Loan Type Selection */}
            <div className="mb-12">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Choose Loan Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(loanTypes).map(([key, loan]) => (
                  <button
                    key={key}
                    onClick={() => setLoanType(key)}
                    className={`p-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                      loanType === key 
                        ? 'text-white shadow-lg scale-105' 
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                    }`}
                    style={{ backgroundColor: loanType === key ? brandColor : undefined }}
                  >
                    {loan.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Left Side - Input Controls */}
              <div className="space-y-10">
                
                {/* Loan Amount with Slider */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="text-lg font-bold text-gray-800">Loan Amount</label>
                    <div className="text-2xl font-bold" style={{ color: brandColor }}>
                      {formatAmount(principal)}
                    </div>
                  </div>
                  
                  {/* Slider */}
                  <div className="mb-6">
                    <input
                      type="range"
                      min={loanTypes[loanType].minAmount}
                      max={loanTypes[loanType].maxAmount}
                      step={loanTypes[loanType].step}
                      value={principal}
                      onChange={(e) => setPrincipal(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, ${brandColor} 0%, ${brandColor} ${((principal - loanTypes[loanType].minAmount) / (loanTypes[loanType].maxAmount - loanTypes[loanType].minAmount)) * 100}%, #e5e7eb ${((principal - loanTypes[loanType].minAmount) / (loanTypes[loanType].maxAmount - loanTypes[loanType].minAmount)) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>{formatAmount(loanTypes[loanType].minAmount)}</span>
                      <span>{formatAmount(loanTypes[loanType].maxAmount)}</span>
                    </div>
                  </div>

                  {/* Preset Amount Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {getPresetAmounts().map(amount => (
                      <button
                        key={amount}
                        onClick={() => setPrincipal(amount)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 ${
                          principal === amount 
                            ? 'text-white shadow-md' 
                            : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                        }`}
                        style={{ backgroundColor: principal === amount ? brandColor : undefined }}
                      >
                        {formatAmount(amount)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Interest Rate with Slider */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="text-lg font-bold text-gray-800">Interest Rate (% per year)</label>
                    <div className="text-2xl font-bold" style={{ color: brandColor }}>
                      {interestRate}%
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <input
                      type="range"
                      min={loanTypes[loanType].minRate}
                      max={loanTypes[loanType].maxRate}
                      step={0.1}
                      value={interestRate}
                      onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, ${brandColor} 0%, ${brandColor} ${((interestRate - loanTypes[loanType].minRate) / (loanTypes[loanType].maxRate - loanTypes[loanType].minRate)) * 100}%, #e5e7eb ${((interestRate - loanTypes[loanType].minRate) / (loanTypes[loanType].maxRate - loanTypes[loanType].minRate)) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>{loanTypes[loanType].minRate}%</span>
                      <span>{loanTypes[loanType].maxRate}%</span>
                    </div>
                  </div>
                </div>

                {/* Tenure with Slider */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="text-lg font-bold text-gray-800">Loan Tenure</label>
                    <div className="text-2xl font-bold" style={{ color: brandColor }}>
                      {tenure} months ({Math.round(tenure / 12 * 10) / 10} years)
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <input
                      type="range"
                      min={loanTypes[loanType].minTenure}
                      max={loanTypes[loanType].maxTenure}
                      step={6}
                      value={tenure}
                      onChange={(e) => setTenure(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, ${brandColor} 0%, ${brandColor} ${((tenure - loanTypes[loanType].minTenure) / (loanTypes[loanType].maxTenure - loanTypes[loanType].minTenure)) * 100}%, #e5e7eb ${((tenure - loanTypes[loanType].minTenure) / (loanTypes[loanType].maxTenure - loanTypes[loanType].minTenure)) * 100}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>{loanTypes[loanType].minTenure} months</span>
                      <span>{loanTypes[loanType].maxTenure} months</span>
                    </div>
                  </div>
                  
                  {/* Quick Tenure Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {[6, 12, 24, 36, 60].filter(months => months >= loanTypes[loanType].minTenure && months <= loanTypes[loanType].maxTenure).map(months => (
                      <button
                        key={months}
                        onClick={() => setTenure(months)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 ${
                          tenure === months 
                            ? 'text-white shadow-md' 
                            : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                        }`}
                        style={{ backgroundColor: tenure === months ? brandColor : undefined }}
                      >
                        {months < 12 ? `${months}m` : `${Math.round(months / 12)}y`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Side - Results */}
              <div className="space-y-8">
                
                {/* EMI Result - Big and Clear */}
                <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border-2 border-purple-100">
                  <h4 className="text-lg font-semibold text-gray-600 mb-2">Monthly EMI</h4>
                  <div className="text-6xl font-bold mb-4" style={{ color: brandColor }}>
                    ₹{emi.toLocaleString('en-IN')}
                  </div>
                  <p className="text-gray-600">for {tenure} months</p>
                </div>

                {/* Breakdown */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                    <span className="font-semibold text-gray-700">Principal Amount</span>
                    <span className="text-xl font-bold text-gray-800">₹{principal.toLocaleString('en-IN')}</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                    <span className="font-semibold text-gray-700">Total Interest</span>
                    <span className="text-xl font-bold text-orange-600">₹{totalInterest.toLocaleString('en-IN')}</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                    <span className="font-bold text-gray-800">Total Amount</span>
                    <span className="text-2xl font-bold text-green-600">₹{totalAmount.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Apply Button */}
                <div className="text-center">
                  <a
                    href="/contact"
                    className="inline-block px-8 py-4 text-lg font-bold text-white rounded-xl hover:opacity-90 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 shadow-lg"
                    style={{ backgroundColor: brandColor }}
                  >
                    Apply for This Loan
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EMICalculator;